// FAQ data for schema markup

export interface FAQItem {
  question: string;
  answer: string;
}

// BBQ & Outdoor Kitchen FAQs
export const bbqFAQs: FAQItem[] = [
  {
    question: "What types of outdoor kitchens do you sell?",
    answer: "We offer custom BBQ islands, modular outdoor kitchens, built-in islands, premium grills (gas, charcoal, Argentine), smokers (pellet, offset, vertical, kamado), pizza ovens, and outdoor refrigeration.",
  },
  {
    question: "Do you offer a lifetime warranty on BBQ islands?",
    answer: "Yes, our premium BBQ islands come with a lifetime warranty. This covers the island structure and frame, giving you peace of mind with your outdoor kitchen investment.",
  },
  {
    question: "Can you help design my outdoor kitchen?",
    answer: "Yes! We offer free design consultations with our outdoor kitchen specialists. We'll help you plan the perfect setup for your backyard, cooking style, and budget.",
  },
  {
    question: "Do you install outdoor kitchens?",
    answer: "We offer white-glove delivery and professional installation services throughout the DFW metroplex. Our team ensures your outdoor kitchen is set up correctly and ready to use.",
  },
  {
    question: "What is the average savings on outdoor kitchens?",
    answer: "Our factory direct model offers savings of 60-70% compared to traditional retailers. Visit our showroom for personalized quotes on your dream outdoor kitchen.",
  },
  {
    question: "Do you sell grills and smokers separately?",
    answer: "Yes, we sell individual grills, smokers, pizza ovens, and accessories in addition to complete outdoor kitchen packages. All items are available at factory direct prices.",
  },
];

// Showroom & General FAQs
export const showroomFAQs: FAQItem[] = [
  {
    question: "Where is your showroom located?",
    answer: "Our showroom is located at 2707 Realty Road, Suite 200, Carrollton, TX 75006. We're easily accessible from Dallas, Fort Worth, Plano, Frisco, and all DFW areas.",
  },
  {
    question: "What are your showroom hours?",
    answer: "We're open Friday and Saturday 10 AM - 5 PM, and Sunday 10:30 AM - 4 PM. We recommend visiting during the week for personalized attention from our team.",
  },
  {
    question: "Do I need an appointment to visit?",
    answer: "No appointment is necessary! Walk-ins are always welcome. However, if you'd like dedicated time with a design consultant, scheduling ahead ensures availability.",
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept cash, all major credit cards, and offer financing options. Contact us for details on current financing promotions and payment plans.",
  },
  {
    question: "Do you price match?",
    answer: "Our factory direct pricing is already significantly below retail, so we don't typically need to price match. Visit our showroom to compare our quality and pricing firsthand.",
  },
];

// Category-specific FAQs
export const categoryFAQs: Record<string, FAQItem[]> = {
  "grills": [
    {
      question: "What size grill should I buy for my family?",
      answer: "For a family of 4-6, a 30-36 inch grill provides ample cooking space. If you frequently entertain larger groups, consider a 42-48 inch model for more versatility.",
    },
    {
      question: "What's the difference between built-in and freestanding grills?",
      answer: "Built-in grills are designed to be permanently installed in an outdoor kitchen island, while freestanding grills come with their own cart and can be moved. Built-in grills offer a more custom look and typically have better heat retention.",
    },
    {
      question: "How many BTUs do I need in a gas grill?",
      answer: "Look for 75-100 BTUs per square inch of cooking surface. More important than total BTUs is even heat distribution and quality burner construction. A well-designed 60,000 BTU grill can outperform a poorly designed 100,000 BTU model.",
    },
    {
      question: "Which grill brands do you carry?",
      answer: "We carry premium brands including Blaze, Lion Premium Grills, Delta Heat, Coyote, Bull Outdoor, American Outdoor Grill (AOG), Lynx, and Alfresco—all at factory direct prices.",
    },
  ],
  "smokers": [
    {
      question: "What's the best smoker for beginners?",
      answer: "Pellet smokers are ideal for beginners because they offer set-it-and-forget-it convenience with digital temperature control. They produce consistent results with minimal effort while you learn smoking techniques.",
    },
    {
      question: "What's the difference between offset and pellet smokers?",
      answer: "Offset smokers use wood logs or charcoal and require more hands-on attention for temperature control, producing traditional authentic smoke flavor. Pellet smokers use compressed wood pellets and have digital controllers for automated temperature management.",
    },
    {
      question: "How long does it take to smoke a brisket?",
      answer: "A full packer brisket typically takes 10-14 hours at 225-250°F, though timing varies by size and desired tenderness. Plan for about 1-1.5 hours per pound and always cook to temperature (203°F internal) rather than time.",
    },
    {
      question: "Can I use a kamado grill as a smoker?",
      answer: "Yes! Kamado grills like those we carry are excellent smokers due to their ceramic construction which retains heat and moisture exceptionally well. They can maintain low-and-slow temperatures for hours with minimal fuel.",
    },
  ],
  "pizza-ovens": [
    {
      question: "How hot do pizza ovens get?",
      answer: "Our outdoor pizza ovens reach temperatures of 700-900°F, allowing you to cook authentic Neapolitan-style pizza in 60-90 seconds. This high heat creates the perfect charred crust that home ovens cannot achieve.",
    },
    {
      question: "What can I cook in a pizza oven besides pizza?",
      answer: "Pizza ovens are incredibly versatile. You can roast vegetables, bake bread, cook steaks, roast whole chickens, make flatbreads, and even prepare desserts. The intense heat creates unique flavors impossible to replicate in conventional ovens.",
    },
    {
      question: "Do you offer gas or wood-fired pizza ovens?",
      answer: "We offer both! Gas pizza ovens provide convenience and consistent temperatures, while wood-fired ovens deliver authentic flavor and the traditional pizza-making experience. Many customers choose dual-fuel models for ultimate flexibility.",
    },
    {
      question: "How long does it take to heat up a pizza oven?",
      answer: "Gas pizza ovens typically reach cooking temperature in 15-20 minutes. Wood-fired ovens take 30-45 minutes to fully heat the dome and floor. Once hot, they maintain temperature efficiently for extended cooking sessions.",
    },
  ],
  "outdoor-kitchens": [
    {
      question: "How much does an outdoor kitchen cost in Dallas?",
      answer: "Outdoor kitchen costs vary widely based on size and features. Our factory direct pricing saves 50-60% compared to traditional retailers. Visit our Carrollton showroom for specific pricing on configurations that match your needs and budget.",
    },
    {
      question: "Do I need a permit for an outdoor kitchen in Texas?",
      answer: "Most Texas cities require permits for gas line installation and electrical work. The grill and island structure typically don't require permits, but requirements vary by city. We recommend checking with your local building department.",
    },
    {
      question: "What materials are best for outdoor kitchens in Texas?",
      answer: "304 stainless steel is the gold standard for Texas outdoor kitchens as it resists rust and corrosion from humidity. For islands, stucco, stone veneer, and tile over a steel frame provide durability against our extreme temperature swings.",
    },
    {
      question: "How long does outdoor kitchen installation take?",
      answer: "For pre-built islands, delivery and setup typically takes 1-2 days. Custom built-in installations may take 1-2 weeks depending on complexity. We handle all aspects of delivery and can coordinate with your contractors for gas and electrical.",
    },
  ],
  "refrigeration": [
    {
      question: "Do outdoor refrigerators need special electrical outlets?",
      answer: "Most outdoor refrigerators require a standard 110V GFCI-protected outlet rated for outdoor use. Some larger units may require 15-20 amp dedicated circuits. We recommend consulting an electrician for proper installation.",
    },
    {
      question: "Can indoor refrigerators be used outdoors?",
      answer: "No. Indoor refrigerators are not designed for temperature extremes and will fail prematurely outdoors. Outdoor-rated refrigerators have weatherproof construction, UV-resistant materials, and compressors designed for ambient temperatures from 35-110°F.",
    },
    {
      question: "What's the difference between outdoor refrigerators and beverage coolers?",
      answer: "Outdoor refrigerators maintain temperatures of 35-42°F for food storage, while beverage coolers typically run warmer (35-50°F) and feature glass doors for display. For a complete outdoor kitchen, we recommend a full refrigerator for food safety.",
    },
    {
      question: "How do I protect my outdoor refrigerator in winter?",
      answer: "In North Texas, you don't typically need to winterize outdoor refrigerators as temperatures rarely stay below freezing for extended periods. However, we recommend keeping units plugged in and running year-round for optimal performance.",
    },
  ],
  "accessories": [
    {
      question: "What grill accessories are essential?",
      answer: "Essential accessories include a quality meat thermometer, grill brush, tongs, spatula, grill cover, and drip pan. For outdoor kitchens, we also recommend side burner covers, warming drawer inserts, and proper lighting.",
    },
    {
      question: "Do you sell replacement grill parts?",
      answer: "Yes, we stock replacement parts for major brands including grates, burners, heat plates, igniters, and rotisserie components. Visit our showroom or contact us with your grill model for availability and pricing.",
    },
    {
      question: "What's the best way to cover and protect my outdoor kitchen?",
      answer: "We offer premium fitted covers for all major grill brands and custom covers for islands. Use covers when the kitchen isn't in use to protect against UV damage, debris, and Texas weather. Pergolas and pavilions provide additional protection.",
    },
  ],
};

// Get FAQs for a specific category
export const getCategoryFAQs = (categorySlug: string): FAQItem[] => {
  return categoryFAQs[categorySlug] || bbqFAQs.slice(0, 4);
};

// Buying guide FAQs (comprehensive)
export const buyingGuideFAQs: FAQItem[] = [
  {
    question: "What size grill do I need for my outdoor kitchen?",
    answer: "For families of 4-6, a 30-36\" grill provides ample cooking space. Larger gatherings may benefit from 42-48\" models. Consider your typical entertaining needs and available island space.",
  },
  {
    question: "What's the best grill material for Texas weather?",
    answer: "304 stainless steel (marine-grade) is the gold standard for Texas outdoor kitchens. It resists rust, corrosion from humidity, and withstands extreme temperature swings.",
  },
  {
    question: "How much does an outdoor kitchen cost?",
    answer: "Outdoor kitchens range from entry-level to premium custom builds. Factory-direct pricing offers 50-60% savings compared to traditional retail. Visit our showroom for current pricing on specific configurations.",
  },
  {
    question: "Do I need a permit for an outdoor kitchen in Texas?",
    answer: "Most Texas cities require permits for gas line installation and electrical work. The grill and island typically don't require permits, but check with your local building department for specific requirements.",
  },
  {
    question: "How long do built-in grills last?",
    answer: "Quality built-in grills from reputable manufacturers typically last 15-20+ years with proper maintenance. Key factors include stainless steel grade, construction quality, and regular cleaning.",
  },
  {
    question: "What accessories should I include in my outdoor kitchen?",
    answer: "Essential accessories include a side burner for sauces, outdoor refrigeration, storage drawers, and a trash pullout. Optional upgrades include power burners, warming drawers, pizza ovens, and smoker boxes.",
  },
];
